import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
  Card,
} from "@mui/material";
import {
  Help as HelpIcon,
  Restaurant as RestaurantIcon,
  QrCode2 as QrCode2Icon,
  Payments as PaymentsIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  VideoLibrary as VideoLibraryIcon,
  Article as ArticleIcon,
  Download as DownloadIcon,
  LiveHelp as LiveHelpIcon,
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  Support as SupportIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import { Link } from "react-router-dom";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import PageTitle from '../components/PageTitle';
import helpBanner from '../assets/images/help.jpeg';


const Help = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState("");

  const helpCategories = [
    {
      icon: <RestaurantIcon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "Getting Started",
      desc: "Setup your restaurant profile",
      count: 6,
      link: "#",
    },
    {
      icon: <QrCode2Icon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "QR & Menu",
      desc: "Generate QR codes & manage menu",
      count: 5,
      link: "#",
    },
    {
      icon: <ShoppingCartIcon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "Orders & Payments",
      desc: "Manage orders & accept payments",
      count: 4,
      link: "#",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "Staff Management",
      desc: "Add & manage restaurant staff",
      count: 3,
      link: "#",
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "Billing & Subscription",
      desc: "Pricing, billing & payments",
      count: 4,
      link: "#",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32, color: "var(--primary)" }} />,
      title: "Security & Privacy",
      desc: "Data protection & security",
      count: 3,
      link: "#",
    },
  ];

  const quickLinks = [
    {
      icon: <VideoLibraryIcon sx={{ color: "var(--primary)" }} />,
      title: "Video Tutorials",
      desc: "Watch step-by-step guides",
      link: "#",
    },
    {
      icon: <ArticleIcon sx={{ color: "var(--primary)" }} />,
      title: "Knowledge Base",
      desc: "Read detailed articles",
      link: "#",
    },
    {
      icon: <DownloadIcon sx={{ color: "var(--primary)" }} />,
      title: "Downloads",
      desc: "Download resources & guides",
      link: "#",
    },
    {
      icon: <LiveHelpIcon sx={{ color: "var(--primary)" }} />,
      title: "Live Chat",
      desc: "Chat with support team",
      link: "#",
    },
  ];

  const popularArticles = [
    {
      title: "How to register your restaurant",
      category: "Getting Started",
      readTime: "3 min",
    },
    {
      title: "Step-by-step menu setup guide",
      category: "Menu Management",
      readTime: "5 min",
    },
    {
      title: "How to generate QR codes for tables",
      category: "QR Code",
      readTime: "2 min",
    },
    {
      title: "Understanding the billing process",
      category: "Billing",
      readTime: "4 min",
    },
    {
      title: "How to manage staff members",
      category: "Staff Management",
      readTime: "3 min",
    },
  ];

  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "Click on 'Forgot Password' on the login page. Enter your registered email and we'll send you a password reset link. Follow the instructions to create a new password.",
      category: "Account",
    },
    {
      question: "How do I add a new menu item?",
      answer: "Go to Dashboard → Menu Management → Add Item. Fill in the details: name, description, price, category, upload image, and set stock quantity. Click 'Save' to add the item.",
      category: "Menu",
    },
    {
      question: "What should I do if QR codes aren't working?",
      answer: "Check if your subscription is active. QR codes are deactivated if payment is not made within the grace period. Also ensure the QR code image is clearly printed and the link is correct.",
      category: "QR Code",
    },
    {
      question: "How do I view order history?",
      answer: "Go to Dashboard → Orders. You can view all orders, filter by date range, order type (Dine-in/Takeaway/Delivery), and export reports for analysis.",
      category: "Orders",
    },
    {
      question: "Can I edit my restaurant details after registration?",
      answer: "Yes! Go to Settings → Restaurant Profile. You can update your restaurant name, address, contact details, WhatsApp number, and other business information anytime.",
      category: "Profile",
    },
  ];

  const supportOptions = [
    {
      icon: <WhatsAppIcon sx={{ fontSize: 30 }} />,
      title: "WhatsApp Support",
      desc: "Chat with us on WhatsApp",
      action: "Chat Now",
      color: "#25D366",
      href:"https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services.",
      target:"_blank",
    },
    {
      icon: <EmailIcon sx={{ fontSize: 30 }} />,
      title: "Email Support",
      desc: "Write to us anytime",
      action: "Send Email",
      color: "#EA4335",
      href:"mailto:restaurantwala24@gmail.com",
      target:"_blank",
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 30 }} />,
      title: "Phone Support",
      desc: "Call us for urgent help",
      action: "Call Now",
      color: "#34A853",
      href: "tel:+916378788756",
      target:"_blank",
    },
  ];

  return (
    <>
<PageTitle
        title="RestaurantWala Help Center – Support & Guides"
        description="Get help with RestaurantWala through useful guides and support for restaurant setup, menus, orders, billing, QR codes and account management."
        keywords="RestaurantWala help center, restaurant software help, restaurant management support, restaurant POS help"
      />
      <Navbar />
      
      {/* Page Banner */}
      <PageBanner
        title="How Can We Help You?"
        subtitle="Find answers, guides, and support resources"
        chipLabel="HELP CENTER"
         bgImage={helpBanner}
      />

      <Box
        sx={{
          bgcolor: "#ffffff",
          minHeight: "100vh",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          
          {/* Search Bar */}
          <Box
            sx={{
              maxWidth: "600px",
              mx: "auto",
              mb: { xs: 4, md: 5 },
            }}
          >
            <TextField
              fullWidth
              placeholder="Search help articles, guides, and FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "var(--text-light)", mr: 1 }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  bgcolor: "#ffffff",
                  boxShadow: "var(--shadow)",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--primary)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--primary)",
                  },
                },
              }}
            />
          </Box>

          {/* Help Categories */}
          <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
            {helpCategories.map((category, index) => (
              <Grid item size={{xs:12, sm:6, md:4}} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "var(--shadow-orange)",
                      borderColor: "var(--primary)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "14px",
                        bgcolor: "var(--bg-primary-soft)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {category.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "var(--text)",
                          mb: 0.5,
                        }}
                      >
                        {category.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "var(--text-light)",
                          fontSize: "13px",
                          mb: 1,
                        }}
                      >
                        {category.desc}
                      </Typography>
                      <Chip
                        label={`${category.count} articles`}
                        size="small"
                        sx={{
                          bgcolor: "var(--bg-primary-soft)",
                          color: "var(--primary)",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Quick Links */}
          {/* <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.3rem", md: "1.5rem" },
                color: "var(--text)",
                mb: 3,
                textAlign: "center",
              }}
            >
              Quick Access
            </Typography>
            <Grid container spacing={3}>
              {quickLinks.map((link, index) => (
                <Grid item size={{xs:6, sm:3}} key={index}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      borderRadius: "16px",
                      border: "1px solid var(--border)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "var(--shadow-orange)",
                        borderColor: "var(--primary)",
                      },
                    }}
                  >
                    <Box sx={{ fontSize: 40, mb: 1, color: "var(--primary)" }}>
                      {link.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: "var(--text)",
                        mb: 0.5,
                      }}
                    >
                      {link.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "var(--text-light)",
                        fontSize: "12px",
                      }}
                    >
                      {link.desc}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box> */}

          {/* Popular Articles */}
          {/* <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.3rem", md: "1.5rem" },
                color: "var(--text)",
                mb: 3,
                textAlign: "center",
              }}
            >
              Popular Articles
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: "20px",
                bgcolor: "#ffffff",
                border: "1px solid var(--border)",
              }}
            >
              {popularArticles.map((article, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderBottom: index < popularArticles.length - 1 ? "1px solid var(--border)" : "none",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "var(--bg-primary-soft)",
                      borderRadius: "8px",
                    },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ArticleIcon sx={{ color: "var(--primary)", fontSize: 20 }} />
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "var(--text)",
                        }}
                      >
                        {article.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "var(--text-light)",
                          fontSize: "12px",
                        }}
                      >
                        {article.category}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`${article.readTime} read`}
                    size="small"
                    sx={{
                      bgcolor: "var(--bg-primary-soft)",
                      color: "var(--primary)",
                      fontSize: "10px",
                    }}
                  />
                </Box>
              ))}
            </Paper>
          </Box> */}

          {/* FAQ Section */}
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.3rem", md: "1.5rem" },
                color: "var(--text)",
                mb: 3,
                textAlign: "center",
              }}
            >
               Frequently Asked Questions
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: "20px",
                bgcolor: "#ffffff",
                border: "1px solid var(--border)",
              }}
            >
              {faqs.map((faq, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderBottom: index < faqs.length - 1 ? "1px solid var(--border)" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <HelpIcon sx={{ color: "var(--primary)", fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "var(--text)",
                          mb: 0.5,
                        }}
                      >
                        {faq.question}
                      </Typography>
                      <Typography
                        sx={{
                          color: "var(--text-light)",
                          fontSize: "13px",
                          lineHeight: 1.6,
                        }}
                      >
                        {faq.answer}
                      </Typography>
                      <Chip
                        label={faq.category}
                        size="small"
                        sx={{
                          mt: 1,
                          bgcolor: "var(--bg-primary-soft)",
                          color: "var(--primary)",
                          fontSize: "10px",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>

          {/* Support Options */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.3rem", md: "1.5rem" },
              color: "var(--text)",
              mb: 3,
              textAlign: "center",
            }}
          >
             Get Support
          </Typography>
          <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
            {supportOptions?.map((option, index) => (
              <Grid item size={{xs:12, sm:4}} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                    textAlign: "center",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "var(--shadow-orange)",
                      borderColor: option.color,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: `${option.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: option.color,
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "var(--text)",
                      mb: 0.5,
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--text-light)",
                      fontSize: "14px",
                      mb: 2,
                    }}
                  >
                    {option.desc}
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      color: option.color,
                      borderColor: option.color,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: option.color,
                        bgcolor: `${option.color}10`,
                      },
                    }}
                    href= {option.href}
                    target ={option.target}
                  >
                    {option.action}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: "24px",
              background: "linear-gradient(135deg, var(--primary-dark), var(--primary))",
              textAlign: "center",
              boxShadow: "var(--shadow-orange-hover)",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.5rem", md: "2rem" },
                color: "#fff",
                mb: 1,
              }}
            >
              Still Need Help?
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontSize: { xs: "14px", md: "16px" },
                mb: 3,
              }}
            >
              Our support team is available 24/7 to assist you
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                component={Link}
                to="/contact"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: "#fff",
                  color: "var(--primary)",
                  px: 4,
                  py: 1.5,
                  borderRadius: "14px",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "#f0f0f0",
                    transform: "scale(1.05)",
                  },
                }}
              >
                Contact Support
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/faq"
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.5)",
                  px: 4,
                  py: 1.5,
                  borderRadius: "14px",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                View All FAQs
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default Help;